import React, {useState, useMemo} from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';

import AlertDeleteDialog from '../../../../AlertDeleteDialog';
import Co2_InterventionTable from './Co2_InterventionTable';
import Co2_InterventionDialog from './Co2_InterventionDialog';

import {co2_intervention} from '../../../../proptypes/co2_intervention';

import {useTranslation} from 'react-i18next';
import {mergeArraysAndSortByYear} from '../../../../../utils/mergeArraysAndSortByYear';
import {hasCuttingWithoutExtractedMass} from '../../../../../utils/hasCuttingWithoutExtractedMass';

const Co2_InterventionMenu = ({
  minYear,
  maxYear,
  hasInventories,
  co2Interventions,
  newCo2Interventions,
  plantingYear,
  onCo2InterventionAdd,
  onCo2InterventionUpdate,
  onCo2NewInterventionsChange,
  onCalculateCO2,
  onPlantingYearChange
}) => {

  const {t} = useTranslation();

  const [openCo2InterventionDialog, setOpenCo2InterventionDialog] = useState(false);
  const [openCalculationAlert, setOpenCalculationAlert] = useState(false);

  const sx = {
    fab: {
      '&.MuiButtonBase-root': {
        borderRadius: '100%',
        backgroundColor: 'secondary.main',
        mr: 1,
        '&:hover': {
          backgroundColor: 'primary.main',
        }
      },
      '&.Mui-disabled': {
        backgroundColor: '#d7d7d7'
      }
    },
    addIcon: {
      color: 'white'
    },
    button: {
      display: 'flex',
      height: '40px',
      flexDirection: 'row',
      justifyContent: co2Interventions.length ? 'space-between' : 'flex-start'
    }
  };

  //ALL INTERVENTIONS
  const allCO2Interventions = useMemo(() => mergeArraysAndSortByYear(co2Interventions, newCo2Interventions)
    , [co2Interventions, newCo2Interventions]);

  const missingExtractedMass = useMemo(() => hasCuttingWithoutExtractedMass(allCO2Interventions)
    ,[allCO2Interventions]);

  //CALCULATE CO2
  const handleCalculateCO2 = () => {
    setOpenCalculationAlert(!openCalculationAlert);
    onCalculateCO2(allCO2Interventions);
  };

  const disableCalculateButton = missingExtractedMass || !hasInventories;

  return <Box display='flex' flexDirection='column'>
    <Co2_InterventionTable
      co2Interventions={co2Interventions}
      newCo2Interventions={newCo2Interventions}
      onCo2NewInterventionsChange={onCo2NewInterventionsChange}
      onCo2InterventionUpdate={onCo2InterventionUpdate}
    />
    <Box display='flex' flexDirection='row' justifyContent='space-between' alignItems='center' mt={missingExtractedMass ? 1 : 2}>
      <Box display='flex' flexDirection='row' sx={{gap: 2, alignItems: 'center'}}>
        <Box sx={sx.button}>
          <Button variant={disableCalculateButton ? 'outlined' : 'contained'}
            color='secondary'
            disabled={disableCalculateButton}
            onClick={() => setOpenCalculationAlert(!openCalculationAlert)}>{t('Balance de CO2')}</Button>
        </Box>
        {
          missingExtractedMass && <Alert severity="error" sx={{my: 2}}>
            <Typography sx={{fontStyle: 'italic'}}>
              {t('Será necesario incluir los m3 marcados en rojo antes de realizar el cálculo de CO')}<sub>2</sub>
            </Typography>
          </Alert>
        }
      </Box>
      <Fab sx={sx.fab} size='small' onClick={() => setOpenCo2InterventionDialog(!openCo2InterventionDialog)}>
        <AddIcon sx={sx.addIcon}/>
      </Fab>
    </Box>
    <Co2_InterventionDialog
      minYear={minYear}
      maxYear={maxYear}
      plantingYear={plantingYear}
      isCo2InterventionDialogOpen={openCo2InterventionDialog}
      allCo2Interventions={allCO2Interventions}
      onDialogClose={() => setOpenCo2InterventionDialog(!openCo2InterventionDialog)}
      onDialogOpen={() => setOpenCo2InterventionDialog(!openCo2InterventionDialog)}
      onPlantingYearChange={onPlantingYearChange}
      onCo2InterventionAdd={onCo2InterventionAdd}
    />
    {
      openCalculationAlert && <AlertDeleteDialog
        title={t('Calcular CO2')}
        additionalText={t('Una vez efectuado el cálculo, no se podrán modificar las actuaciones. ¿Estás seguro?')}
        onCancel={() => setOpenCalculationAlert(!openCalculationAlert)}
        onAccept={handleCalculateCO2}/>
    }
  </Box>;
};

Co2_InterventionMenu.propTypes = {
  minYear: PropTypes.number.isRequired,
  maxYear: PropTypes.number.isRequired,
  hasInventories: PropTypes.bool,
  co2Interventions: PropTypes.arrayOf(co2_intervention),
  plantingYear: PropTypes.number,
  newCo2Interventions: PropTypes.arrayOf(co2_intervention),
  onCo2InterventionAdd: PropTypes.func.isRequired,
  onCo2InterventionUpdate: PropTypes.func.isRequired,
  onCo2NewInterventionsChange: PropTypes.func.isRequired,
  onCalculateCO2: PropTypes.func.isRequired,
  onPlantingYearChange: PropTypes.func.isRequired
};

export default Co2_InterventionMenu;